import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Copy, Trash2, RefreshCw, TrendingUp, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Message Component
function ChatMessage({ message, onCopy }) {
  const isUser = message.role === 'user';
  
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '16px'
      }}
      data-testid={`message-${message.role}`}
    >
      <div
        style={{
          maxWidth: '75%',
          padding: '16px',
          borderRadius: '12px',
          background: isUser ? 'var(--primary-500)' : 'white',
          color: isUser ? 'white' : 'var(--text-primary)',
          border: isUser ? 'none' : '1px solid var(--border-light)',
          boxShadow: isUser ? '0 2px 8px rgba(59, 130, 246, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {!isUser && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              borderRadius: '6px', 
              background: 'var(--accent-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: '700'
            }}>
              AI
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
              Clariti Assistant
            </span>
          </div>
        )}
        
        <div style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
          {message.content}
        </div>
        
        {/* Data Cards */}
        {message.data && message.data.type === 'campaigns' && message.data.campaigns && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {message.data.campaigns.slice(0, 3).map((campaign, idx) => (
              <div key={idx} style={{ 
                padding: '12px', 
                background: 'var(--bg-tertiary)', 
                borderRadius: '8px',
                border: '1px solid var(--border-medium)'
              }}>
                <p style={{ fontWeight: '600', marginBottom: '4px', fontSize: '0.875rem' }}>
                  {campaign.name}
                </p>
                <div style={{ display: 'flex', gap: '12px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>{campaign.channel}</span>
                  <span>•</span>
                  <span>${(campaign.spend || 0).toLocaleString()}</span>
                  {campaign.effectiveness && (
                    <>
                      <span>•</span>
                      <span>{(campaign.effectiveness * 100).toFixed(0)}% effective</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Analysis Data */}
        {message.data && message.data.type === 'analysis' && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'hsl(180, 65%, 97%)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-700)', marginBottom: '8px' }}>
              📊 Analysis Results
            </p>
            {message.data.benchmarks && (
              <div style={{ fontSize: '0.875rem' }}>
                <p>Avg CPA: ${message.data.benchmarks.avg_cpa}</p>
                <p>Avg CTR: {message.data.benchmarks.avg_ctr}%</p>
              </div>
            )}
          </div>
        )}
        
        {/* Scenario Data */}
        {message.data && message.data.type === 'scenario' && message.data.simulation_results && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'hsl(142, 76%, 96%)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--success-700)', marginBottom: '8px' }}>
              🎯 Simulation Results
            </p>
            <div style={{ fontSize: '0.875rem' }}>
              <p>Expected Return: ${message.data.simulation_results.mean_return?.toLocaleString()}</p>
              <p>Confidence Interval: ${message.data.simulation_results.percentile_5?.toLocaleString()} - ${message.data.simulation_results.percentile_95?.toLocaleString()}</p>
            </div>
          </div>
        )}
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: '12px',
          paddingTop: '8px',
          borderTop: isUser ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--border-light)'
        }}>
          <span style={{ 
            fontSize: '0.75rem', 
            opacity: 0.7 
          }}>
            {message.timestamp.toLocaleTimeString()}
          </span>
          {!isUser && (
            <button
              onClick={() => onCopy(message.content)}
              style={{
                padding: '4px 8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.75rem'
              }}
              title="Copy message"
            >
              <Copy size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Suggested Question Button
function SuggestedQuestion({ question, onClick }) {
  return (
    <button
      onClick={() => onClick(question)}
      style={{
        padding: '10px 16px',
        background: 'white',
        border: '1px solid var(--border-light)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'left',
        color: 'var(--text-primary)',
        fontWeight: '500'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--primary-500)';
        e.currentTarget.style.background = 'hsl(210, 100%, 98%)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-light)';
        e.currentTarget.style.background = 'white';
      }}
      data-testid="suggested-question"
    >
      💡 {question}
    </button>
  );
}

// Main Chat Component
function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadSuggestions();
    // Generate conversation ID
    setConversationId(`conv_${Date.now()}`);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      const response = await axios.get(`${API_BASE_URL}/api/chat/suggestions`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions([
        "What are my top performing campaigns?",
        "Show me campaigns with low effectiveness scores",
        "What's my total marketing spend?",
        "Which channels are most efficient?",
        "Compare my search and social campaigns",
        "What if I increased video budget by 20%?"
      ]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = { 
      role: 'user', 
      content: messageText, 
      timestamp: new Date() 
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/chat/query`, {
        query: messageText,
        conversation_id: conversationId
      });
      
      const aiMessage = { 
        role: 'assistant', 
        content: response.data.answer || 'I apologize, I encountered an issue processing your question.', 
        data: response.data.data,
        intent: response.data.intent,
        timestamp: new Date() 
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or rephrase your question.', 
        timestamp: new Date() 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestionClick = (question) => {
    setInput(question);
    sendMessage(question);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleClearChat = () => {
    if (window.confirm('Clear conversation history?')) {
      setMessages([]);
      setConversationId(`conv_${Date.now()}`);
    }
  };

  return (
    <div className="page-container" style={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">AI Chat Assistant</h1>
            <p className="page-subtitle">Intelligent conversational analytics powered by Clariti AI</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={loadSuggestions}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '8px 16px' }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button 
              onClick={handleClearChat}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '8px 16px' }}
              disabled={messages.length === 0}
            >
              <Trash2 size={16} />
              Clear
            </button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', gap: '24px', marginTop: '24px', overflow: 'hidden' }}>
        {/* Suggestions Sidebar */}
        <div style={{ width: '300px', flexShrink: 0 }}>
          <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} style={{ color: 'var(--accent-500)' }} />
              Suggested Questions
            </h3>
            
            {loadingSuggestions ? (
              <div className="loading">
                <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
                {suggestions.map((question, idx) => (
                  <SuggestedQuestion
                    key={idx}
                    question={question}
                    onClick={handleSuggestionClick}
                  />
                ))}
              </div>
            )}

            {/* Info */}
            <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', lineHeight: '1.5' }}>
                💡 Ask about campaigns, request analysis, compare performance, or explore what-if scenarios
              </p>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {messages.length === 0 ? (
              <div className="empty-state">
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '20px', 
                  background: 'var(--accent-500)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: 'white',
                  fontSize: '2rem'
                }}>
                  💬
                </div>
                <p style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
                  Start a Conversation with Clariti AI
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', maxWidth: '500px', margin: '0 auto 24px' }}>
                  I can help you analyze campaigns, understand AI recommendations, run what-if scenarios, and answer questions about your marketing performance.
                </p>
                
                {/* Quick Start Examples */}
                <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '12px', color: 'var(--text-secondary)' }}>
                    Try asking:
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ 
                      padding: '12px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'left'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                        <BarChart3 size={16} style={{ color: 'var(--primary-500)' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--primary-600)' }}>
                          Campaign Analysis
                        </span>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        "What are my top performing campaigns?"
                      </p>
                    </div>
                    
                    <div style={{ 
                      padding: '12px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'left'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                        <TrendingUp size={16} style={{ color: 'var(--success-500)' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--success-600)' }}>
                          What-If Scenarios
                        </span>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        "What if I reduce social budget by 15%?"
                      </p>
                    </div>
                    
                    <div style={{ 
                      padding: '12px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'left'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                        <Sparkles size={16} style={{ color: 'var(--accent-500)' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-600)' }}>
                          AI Insights
                        </span>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        "Why did you rank Campaign A above B?"
                      </p>
                    </div>
                    
                    <div style={{ 
                      padding: '12px', 
                      background: 'var(--bg-secondary)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'left'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                        <BarChart3 size={16} style={{ color: 'var(--warning-500)' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--warning-600)' }}>
                          Comparisons
                        </span>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        "Compare search vs social performance"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <ChatMessage
                    key={idx}
                    message={msg}
                    onCopy={handleCopy}
                  />
                ))}
                
                {/* Typing Indicator */}
                {loading && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                    <div style={{ 
                      padding: '16px 20px', 
                      background: 'white', 
                      borderRadius: '12px',
                      border: '1px solid var(--border-light)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: 'var(--accent-500)',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }}></div>
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: 'var(--accent-500)',
                        animation: 'pulse 1.5s ease-in-out infinite 0.2s'
                      }}></div>
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: 'var(--accent-500)',
                        animation: 'pulse 1.5s ease-in-out infinite 0.4s'
                      }}></div>
                      <span style={{ marginLeft: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Clariti is thinking...
                      </span>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div style={{ borderTop: '1px solid var(--border-light)', padding: '20px 24px', background: 'var(--bg-secondary)' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="Ask about campaigns, request analysis, or explore scenarios..."
                    disabled={loading}
                    rows={1}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      resize: 'none',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      minHeight: '48px',
                      maxHeight: '120px'
                    }}
                    data-testid="chat-input"
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '6px' }}>
                    Press Enter to send, Shift+Enter for new line
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="btn btn-primary"
                  style={{ 
                    padding: '14px 24px',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  data-testid="send-message-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                      Thinking...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

export default Chat;
